import { AuthSelectors, LanguageSelectors } from '@web_opxp/features';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AppSelectors } from '../app';
import { BridgeSelectors } from '../bridge';
import { CyclesSelectors } from '../cycles';
import { SessionSelectors } from '../session';
import { SubscriptionSelectors } from '../subscription';
import { isBannerCanBeShown, subscriptionIsOlderThanAMonth } from './service';

export default function useMedBannerToggle(): [boolean, (toggle: boolean) => void] {
  const [showMedBanner, setShowMedBanner] = useState(false);
  const [loadingTimeoutPassed, setLoadingTimeoutPassed] = useState(false);
  // banner must be shown only to premium users
  const isPremium = useSelector(AuthSelectors.isPremium);
  // banner must be shown only when 1 month after purchase have passed
  const subscriptionCreatedAt = useSelector(SubscriptionSelectors.getSubscriptionCreationDate);
  const subscriptionCondition = isPremium && subscriptionIsOlderThanAMonth(subscriptionCreatedAt);
  // banner must be hidden when other banners on dashboard are shown
  const carouselBannerShown = useSelector(AppSelectors.isBannerShown);
  // banner must be hidden if bridge is shown
  const bridgeShown = useSelector(BridgeSelectors.isBridgeActive);
  // banner must be hidden when page loading
  const appLoading = useSelector(AppSelectors.isAppLoading);
  const localeLoading = useSelector(LanguageSelectors.isLoading);
  const cyclesLoading = useSelector(CyclesSelectors.isCyclesLoading);
  const sessionLoading = useSelector(SessionSelectors.isLoading);
  const { pathname } = useLocation();
  useEffect(() => {
    // banner must be shown only on dashboard page
    const isDashboard = pathname === '/' || pathname === '/dashboard';
    // banner must be shown only when all important data finished loading
    const loading = appLoading || localeLoading || cyclesLoading || sessionLoading;
    // banner must be shown only for subscribers and when no other banner or bridge is shown
    const canShowFromState = !loading && subscriptionCondition && !carouselBannerShown && !bridgeShown;
    // wait a little bit to let other features to be loaded, like other banners for example
    if (!canShowFromState || !isDashboard || !loadingTimeoutPassed) {
      setShowMedBanner(false);
      return;
    }
    // if all conditions are met - check for timestamps
    const timeoutCorrect = isBannerCanBeShown();
    setShowMedBanner(timeoutCorrect);
  }, [
    subscriptionCondition,
    carouselBannerShown,
    bridgeShown,
    pathname,
    appLoading,
    localeLoading,
    cyclesLoading,
    sessionLoading,
    loadingTimeoutPassed,
  ]);
  // we set a timeout for half a second to make sure that other features are loaded
  // and we can decide if banner should be shown
  useEffect(() => {
    const timeoutId = setTimeout(() => setLoadingTimeoutPassed(true), 500);
    return () => clearTimeout(timeoutId);
  }, []);
  return [showMedBanner, setShowMedBanner];
}
